/**
 * AppErrorBoundary — Cattura crash React globali per evitare la
 * temuta "pagina bianca". Mostra fallback con istruzioni e bottoni.
 *
 * Da wrappare attorno a <Routes /> in App.js.
 */
import { Component } from "react";

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] crash React:", error, errorInfo);
    // Best-effort log al backend (non bloccare se fallisce)
    try {
      fetch("/api/_client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || "").slice(0, 4000),
          path: typeof window !== "undefined" ? window.location.pathname : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {});
    } catch { /* noop */ }
  }

  reload = () => {
    try {
      if ("caches" in window) {
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
      }
    } catch { /* noop */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900">Ops, qualcosa è andato storto</h1>
          <p className="text-sm text-slate-600 mt-2">
            La pagina ha avuto un errore inaspettato. Prova a ricaricare. Se persiste, scrivi a Sara.
          </p>

          {this.state.error?.message && (
            <details className="text-left mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              <summary className="cursor-pointer font-semibold text-slate-700">Dettagli tecnici</summary>
              <pre className="whitespace-pre-wrap break-all mt-2">{String(this.state.error.message)}</pre>
            </details>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={this.reload}
              className="flex-1 h-11 rounded-lg bg-amber-500 hover:bg-amber-600 text-[#13294B] font-bold transition"
              data-testid="error-boundary-reload"
            >
              Ricarica
            </button>
            <a
              href="mailto:info@sagigrp.it?subject=Pagina%20bianca%20portale%20HACCP"
              className="flex-1 h-11 rounded-lg border border-slate-300 text-slate-800 bg-white hover:bg-slate-50 flex items-center justify-center font-semibold transition"
              data-testid="error-boundary-email"
            >
              Segnala via Email
            </a>
          </div>

          <p className="text-[11px] text-slate-400 mt-4">
            Email: <a href="mailto:info@sagigrp.it" className="underline">info@sagigrp.it</a>
          </p>
        </div>
      </div>
    );
  }
}
